import { useEffect, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import type { PublicEvent } from "../types/public";
import { getVisitorSubmitterKey } from "../lib/visitor-submitter";

type VisitorModal = null | { t: "add" } | { t: "correction"; ev: PublicEvent };

const VISITOR_TIME_HOURS = Array.from({ length: 30 }, (_, i) => String(i).padStart(2, "0"));
const VISITOR_TIME_MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
const JAPAN_PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県", "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県", "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県", "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
];

const emptyAddDraft = {
  fan_group_name: "",
  fan_artist_name: "",
  event_date: "",
  prefecture: "",
  performance_count: "1",
  detail_url: "",
  source_title: "",
  message: "",
};

const emptyCorrectionDraft = {
  message: "",
  venue: "",
  event_name: "",
  event_date: "",
  time_start: "",
  detail_url: "",
};

function isAllowedSourceUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && (url.hostname === "helloproject.com" || url.hostname === "www.jp-r.co.jp");
  } catch {
    return false;
  }
}

function splitVisitorTime(value: string): { hour: string; minute: string } {
  const m = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return { hour: "", minute: "" };
  return { hour: m[1]!.padStart(2, "0"), minute: m[2]! };
}

function pushPatchIfChanged(patch: Record<string, string>, key: string, next: string, prev: string | null | undefined) {
  const t = next.trim();
  if (!t) return;
  if (t === String(prev ?? "").trim()) return;
  patch[key] = t;
}

export function useVisitorEventRequestModals(): {
  openAddModal: (eventDate?: string) => void;
  openCorrectionModal: (ev: PublicEvent) => void;
  visitorModalEl: ReactElement | null;
} {
  const [visitorModal, setVisitorModal] = useState<VisitorModal>(null);
  const [visitorSubmitting, setVisitorSubmitting] = useState(false);
  const [visitorFormErr, setVisitorFormErr] = useState<string | null>(null);
  const [titleFetching, setTitleFetching] = useState(false);
  const [titleFetchMsg, setTitleFetchMsg] = useState<string | null>(null);
  const [addDraft, setAddDraft] = useState(emptyAddDraft);
  const [corrDraft, setCorrDraft] = useState(emptyCorrectionDraft);

  useEffect(() => {
    if (!visitorModal) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !visitorSubmitting) setVisitorModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visitorModal, visitorSubmitting]);

  const postVisitorRequest = async (payload: Record<string, unknown>) => {
    const res = await fetch("/api/public/event-requests", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) {
      if (j.error === "duplicate_event") throw new Error("この予定はすでに登録されています。重複投稿は反映しません。");
      if (j.error === "rate_limited") throw new Error("短時間の投稿回数が上限に達しました。時間をおいてからもう一度お願いします。");
      throw new Error(j.error ?? `HTTP ${res.status}`);
    }
  };

  const fetchSourceTitle = async (url: string) => {
    const trimmed = url.trim();
    setTitleFetchMsg(null);
    if (!trimmed) return;
    if (!isAllowedSourceUrl(trimmed)) {
      setTitleFetchMsg("URLは helloproject.com または www.jp-r.co.jp の https ページだけ登録できます。");
      return;
    }
    setTitleFetching(true);
    try {
      const res = await fetch("/api/public/source-title", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
      });
      const j = (await res.json().catch(() => ({}))) as { title?: string | null; error?: string };
      if (!res.ok) throw new Error(j.error ?? `HTTP ${res.status}`);
      if (j.title) {
        setAddDraft((d) => ({ ...d, source_title: j.title ?? "" }));
        setTitleFetchMsg("タイトルを取得しました。タイトルはURLから取得したものだけ使います。");
      } else {
        setTitleFetchMsg("タイトルを取得できませんでした。このURLでは投稿できません。");
      }
    } catch {
      setTitleFetchMsg("タイトルを取得できませんでした。このURLでは投稿できません。");
    } finally {
      setTitleFetching(false);
    }
  };

  const submitVisitorAdd = async () => {
    setVisitorSubmitting(true);
    setVisitorFormErr(null);
    if ((!addDraft.fan_group_name.trim() && !addDraft.fan_artist_name.trim()) || !addDraft.event_date.trim() || !addDraft.prefecture || !addDraft.source_title.trim()) {
      setVisitorFormErr("未入力箇所があります");
      setVisitorSubmitting(false);
      return;
    }
    if (!isAllowedSourceUrl(addDraft.detail_url)) {
      setVisitorFormErr("情報元URLは https://helloproject.com または https://www.jp-r.co.jp 配下だけ登録できます");
      setVisitorSubmitting(false);
      return;
    }
    try {
      await postVisitorRequest({
        kind: "add",
        message: addDraft.message.trim() || "（本文なし）",
        submitterKey: getVisitorSubmitterKey(),
        proposed: {
          fan_group_name: addDraft.fan_group_name.trim(),
          fan_artist_name: addDraft.fan_artist_name.trim(),
          event_date: addDraft.event_date.trim(),
          prefecture: addDraft.prefecture,
          performance_count: Number(addDraft.performance_count),
          detail_url: addDraft.detail_url.trim(),
          source_title: addDraft.source_title.trim(),
          event_name: addDraft.source_title.trim(),
        },
      });
      setVisitorModal(null);
    } catch (e) {
      setVisitorFormErr(String(e));
    } finally {
      setVisitorSubmitting(false);
    }
  };

  const submitVisitorCorrection = async (ev: PublicEvent) => {
    setVisitorSubmitting(true);
    setVisitorFormErr(null);
    try {
      const msg = corrDraft.message.trim();
      if (!msg) {
        setVisitorFormErr("依頼内容（メッセージ）を入力してください");
        setVisitorSubmitting(false);
        return;
      }
      const patch: Record<string, string> = {};
      pushPatchIfChanged(patch, "venue", corrDraft.venue, ev.venue);
      pushPatchIfChanged(patch, "event_name", corrDraft.event_name, ev.event_name);
      pushPatchIfChanged(patch, "event_date", corrDraft.event_date, ev.event_date);
      pushPatchIfChanged(patch, "time_start", corrDraft.time_start, ev.time_start);
      pushPatchIfChanged(patch, "detail_url", corrDraft.detail_url, ev.detail_url);
      await postVisitorRequest({ kind: "correction", event_id: ev.id, message: msg, patch, submitterKey: getVisitorSubmitterKey() });
      setVisitorModal(null);
    } catch (e) {
      setVisitorFormErr(String(e));
    } finally {
      setVisitorSubmitting(false);
    }
  };

  const openCorrectionModal = (ev: PublicEvent) => {
    setVisitorFormErr(null);
    setCorrDraft(emptyCorrectionDraft);
    setVisitorModal({ t: "correction", ev });
  };

  const openAddModal = (eventDate = "") => {
    setVisitorFormErr(null);
    setTitleFetchMsg(null);
    setAddDraft({ ...emptyAddDraft, event_date: eventDate });
    setVisitorModal({ t: "add" });
  };

  const corrTime = splitVisitorTime(corrDraft.time_start);
  const setCorrTimePart = (part: "hour" | "minute", next: string) => {
    const hour = part === "hour" ? next : corrTime.hour;
    const minute = part === "minute" ? next : corrTime.minute;
    setCorrDraft((d) => ({ ...d, time_start: !hour && !minute ? "" : `${hour || "00"}:${minute || "00"}` }));
  };

  const visitorModalContent: ReactElement | null = visitorModal ? (
    <div
      className="home-visitor-modal-root"
      role="presentation"
      onClick={() => {
        if (!visitorSubmitting) setVisitorModal(null);
      }}
    >
      <div
        className="home-visitor-modal-panel card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-visitor-modal-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        {visitorModal.t === "add" ? (
          <>
            <h2 id="home-visitor-modal-title" className="home-visitor-modal-title">
              こんなイベントもあるよ！
            </h2>
            <p className="home-visitor-modal-lead">ハロプロ公式またはJ.P ROOM公式に載っている予定を投稿できます。投稿後はすぐ公開され、必要があれば管理側で後から直します。</p>
            <p className="home-visitor-modal-lead" style={{ fontSize: 12, marginTop: -4 }}>
              URLは https://helloproject.com または https://www.jp-r.co.jp 配下のみ登録できます。
            </p>
            <p className="home-submit-limit-note">入力回数制限: 1時間に3件まで / 1日に10件まで</p>
            {visitorFormErr ? <p className="home-visitor-modal-err">{visitorFormErr}</p> : null}
            <label className="home-visitor-field">
              <span>グループ名</span>
              <input
                className="input"
                value={addDraft.fan_group_name}
                onChange={(e) => setAddDraft((d) => ({ ...d, fan_group_name: e.target.value }))}
                maxLength={120}
              />
            </label>
            <label className="home-visitor-field">
              <span>アーティスト名</span>
              <input
                className="input"
                value={addDraft.fan_artist_name}
                onChange={(e) => setAddDraft((d) => ({ ...d, fan_artist_name: e.target.value }))}
                maxLength={120}
              />
            </label>
            <label className="home-visitor-field">
              <span>開催日（必須）</span>
              <input
                className="input"
                type="date"
                value={addDraft.event_date}
                onChange={(e) => setAddDraft((d) => ({ ...d, event_date: e.target.value }))}
              />
            </label>
            <label className="home-visitor-field">
              <span>開催都道府県（必須）</span>
              <select className="input" value={addDraft.prefecture} onChange={(e) => setAddDraft((d) => ({ ...d, prefecture: e.target.value }))}>
                <option value="">選択してください</option>
                {JAPAN_PREFECTURES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="home-visitor-field">
              <span>公演数（必須・数字）</span>
              <input className="input" type="number" min={1} max={99} value={addDraft.performance_count} onChange={(e) => setAddDraft((d) => ({ ...d, performance_count: e.target.value }))} />
            </label>
            <label className="home-visitor-field">
              <span>情報元URL（必須）</span>
              <input
                className="input"
                value={addDraft.detail_url}
                onChange={(e) => {
                  const next = e.target.value;
                  setAddDraft((d) => ({ ...d, detail_url: next, source_title: "" }));
                  setTitleFetchMsg(null);
                }}
                onBlur={() => void fetchSourceTitle(addDraft.detail_url)}
                maxLength={2000}
                placeholder="https://helloproject.com/... または https://www.jp-r.co.jp/..."
              />
            </label>
            <button type="button" className="btn small secondary" disabled={titleFetching || !addDraft.detail_url.trim()} onClick={() => void fetchSourceTitle(addDraft.detail_url)}>
              {titleFetching ? "タイトル取得中…" : "URLからタイトル取得"}
            </button>
            {titleFetchMsg ? <p className="home-visitor-modal-lead" style={{ fontSize: 12 }}>{titleFetchMsg}</p> : null}
            <label className="home-visitor-field">
              <span>タイトル（URLから自動取得のみ）</span>
              <input className="input" value={addDraft.source_title} readOnly placeholder="URLからタイトル取得を押してください" maxLength={600} />
            </label>
            <label className="home-visitor-field">
              <span>補足・出典メモ（任意）</span>
              <textarea
                className="input home-visitor-textarea"
                rows={3}
                value={addDraft.message}
                onChange={(e) => setAddDraft((d) => ({ ...d, message: e.target.value }))}
                maxLength={4000}
                placeholder="例：〇〇のニュースより。追記したい情報があればここに。"
              />
            </label>
            <div className="home-visitor-modal-actions">
              <button type="button" className="btn secondary" disabled={visitorSubmitting} onClick={() => setVisitorModal(null)}>
                キャンセル
              </button>
              <button
                type="button"
                className="btn secondary"
                disabled={visitorSubmitting}
                onClick={() => {
                  setAddDraft(emptyAddDraft);
                  setTitleFetchMsg(null);
                  setVisitorFormErr(null);
                }}
              >
                内容クリア
              </button>
              <button type="button" className="btn" disabled={visitorSubmitting} onClick={() => void submitVisitorAdd()}>
                {visitorSubmitting ? "投稿中…" : "投稿して即公開"}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="home-visitor-modal-title" className="home-visitor-modal-title">
              間違ってるよ！
            </h2>
            <p className="home-visitor-modal-lead">間違っている情報があったら教えてください。変更したい項目だけ入力できます。</p>
            <p className="home-visitor-modal-lead mono" style={{ fontSize: 12, marginTop: -4 }}>
              {visitorModal.ev.event_date} ・ {visitorModal.ev.event_name}
            </p>
            <p className="home-visitor-modal-lead" style={{ fontSize: 12, marginTop: -4 }}>
              依頼内容（メッセージ）は必須です。送信後、運営が確認してから反映します。
            </p>
            <p className="home-submit-limit-note">入力回数制限: 1時間に5件まで / 1日に20件まで</p>
            {visitorFormErr ? <p className="home-visitor-modal-err">{visitorFormErr}</p> : null}
            <label className="home-visitor-field">
              <span>依頼内容（必須）</span>
              <textarea
                className="input home-visitor-textarea"
                rows={4}
                value={corrDraft.message}
                onChange={(e) => setCorrDraft((d) => ({ ...d, message: e.target.value }))}
                maxLength={4000}
                placeholder="例：会場名が公式と違います。開演時刻が19:00に変更されていました。"
              />
            </label>
            <label className="home-visitor-field">
              <span>イベント名（訂正後・任意）</span>
              <input className="input" value={corrDraft.event_name} onChange={(e) => setCorrDraft((d) => ({ ...d, event_name: e.target.value }))} maxLength={600} />
            </label>
            <label className="home-visitor-field">
              <span>開催日（訂正後・任意・YYYY-MM-DD）</span>
              <input className="input" type="date" value={corrDraft.event_date} onChange={(e) => setCorrDraft((d) => ({ ...d, event_date: e.target.value }))} />
            </label>
            <label className="home-visitor-field">
              <span>会場・施設名（訂正後・任意）</span>
              <input className="input" value={corrDraft.venue} onChange={(e) => setCorrDraft((d) => ({ ...d, venue: e.target.value }))} maxLength={400} />
            </label>
            <label className="home-visitor-field">
              <span>開始時刻（訂正後・任意）</span>
              <div className="admin-time-picker-control">
                <select className="input admin-time-picker-select" value={corrTime.hour} onChange={(e) => setCorrTimePart("hour", e.target.value)}>
                  <option value="">時</option>
                  {VISITOR_TIME_HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <span className="admin-time-picker-colon">:</span>
                <select className="input admin-time-picker-select" value={corrTime.minute} onChange={(e) => setCorrTimePart("minute", e.target.value)}>
                  <option value="">分</option>
                  {VISITOR_TIME_MINUTES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn secondary admin-time-picker-clear" onClick={() => setCorrDraft((d) => ({ ...d, time_start: "" }))}>
                  クリア
                </button>
              </div>
            </label>
            <label className="home-visitor-field">
              <span>詳細 URL（訂正後・任意）</span>
              <input className="input" value={corrDraft.detail_url} onChange={(e) => setCorrDraft((d) => ({ ...d, detail_url: e.target.value }))} maxLength={2000} />
            </label>
            <div className="home-visitor-modal-actions">
              <button type="button" className="btn secondary" disabled={visitorSubmitting} onClick={() => setVisitorModal(null)}>
                キャンセル
              </button>
              <button
                type="button"
                className="btn secondary"
                disabled={visitorSubmitting}
                onClick={() => {
                  setCorrDraft(emptyCorrectionDraft);
                  setVisitorFormErr(null);
                }}
              >
                内容クリア
              </button>
              <button type="button" className="btn" disabled={visitorSubmitting} onClick={() => void submitVisitorCorrection(visitorModal.ev)}>
                {visitorSubmitting ? "送信中…" : "送信する"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;
  const visitorModalEl: ReactElement | null =
    visitorModalContent && typeof document !== "undefined" ? createPortal(visitorModalContent, document.body) : visitorModalContent;

  return { openAddModal, openCorrectionModal, visitorModalEl };
}
